import React from 'react';

type UpfrontPricingType = {
	HasData: boolean;
	HasMemberData: boolean;
	MemberUpfrontPaymentAmount?: number | null;
	UpfrontPaymentAmount?: number | null;
	UpfrontPaymentPercentage?: number | null;
};

type FinanceInstallmentProps = {
	UpfrontPricing: UpfrontPricingType;
	UseUpfrontPricingAmounts: boolean;
	NewDayFinanceQuote: any;
	isSwitch24Product: boolean;
	switchMonthlyPayment: number;
	useEnhancedFinanceProposition?: boolean;
	isFinancePromotion?: boolean;
};

export function FinanceInstallments({
	UpfrontPricing = {
		HasData: false,
		HasMemberData: false,
	},
	UseUpfrontPricingAmounts,
	NewDayFinanceQuote,
	isSwitch24Product,
	switchMonthlyPayment,
	useEnhancedFinanceProposition,
	isFinancePromotion,
}: FinanceInstallmentProps) {
	const monthlyPrice = isSwitch24Product
		? `£${switchMonthlyPayment?.toFixed(2)}`
		: NewDayFinanceQuote.FormattedMonthlyPriceString;

	const upfrontCriteriaMet = UseUpfrontPricingAmounts && UpfrontPricing.HasData;

	const correctUpfrontValue = isSwitch24Product
		? UpfrontPricing?.MemberUpfrontPaymentAmount ||
		  UpfrontPricing?.UpfrontPaymentAmount
		: UpfrontPricing?.UpfrontPaymentAmount;

	if (useEnhancedFinanceProposition) {
		const upfrontText = upfrontCriteriaMet
			? `& £${correctUpfrontValue?.toFixed(2)} upfront today`
			: '';

		const monthlyBlock = (
			<span className="text-body text-water">
				<span className="text-body-xs lg:text-body-sm text-water">From </span>
				<span className="font-bold text-lg lg:text-xl">
					{monthlyPrice}
				</span>
				<span className="text-body-xs lg:text-base font-bold text-water"> per month*</span>
			</span>
		);

		let enhancedContent = monthlyBlock;

		if (isFinancePromotion && !isSwitch24Product) {
			enhancedContent = (
				<span className="text-body-sm text-water">{`or from ${monthlyPrice} monthly*`}</span>
			);
		} else if (upfrontText) {
			enhancedContent = (
				<>
					<div>{monthlyBlock}</div>
					<span
						className="text-body-xs leading-none lg:text-body-sm lg:leading-none text-text-primary"
						data-testid="finance-upfront-text">
						{upfrontText}
					</span>
				</>
			);
		}

		return (
			<span data-testid="finance-text-with-optional-upfront">
				{enhancedContent}
			</span>
		);
	}

	let financeText = `from ${monthlyPrice} monthly*`;

	if (upfrontCriteriaMet) {
		const formattedUpfront = correctUpfrontValue?.toFixed(2);
		financeText = isSwitch24Product
			? `from ${monthlyPrice} monthly*\nplus £${formattedUpfront} upfront`
			: `from ${monthlyPrice} monthly* plus\n£${formattedUpfront} upfront today*`;
	}

	return (
		<div
			className={upfrontCriteriaMet ? 'whitespace-pre-line leading-normal' : ''}
			data-testid="finance-text-with-optional-upfront">
			{financeText}
			<span
				className={isSwitch24Product ? '' : 'ico ico-chevron-right ico-lg'}
				data-testid="optional-chevron-element"
			/>
		</div>
	);
}

FinanceInstallments.defaultProps = {
	useEnhancedFinanceProposition: false,
	isFinancePromotion: false,
};
